import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle, Calendar, MapPin, Users, Phone, Mail } from 'lucide-react';
import Button from '../components/UI/Button';
import Card from '../components/UI/Card';
import { useTranslation } from 'react-i18next';
import { formatMad } from '../utils/currency';

const BookingSuccessScreen = () => {
  const location = useLocation();
  const [bookingData, setBookingData] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    // Get booking data from navigation state
    if (location.state && location.state.booking) {
      setBookingData(location.state.booking);
    }
  }, [location]);

  if (!bookingData) {
    return (
      <div className="min-h-screen   flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-sofia font-bold text-primary mb-4">
            {t('bookingSuccess.notFoundTitle')}
          </h1>
          <p className="text-text-secondary font-sofia mb-6">
            {t('bookingSuccess.notFoundMessage')}
          </p>
          <Link to="/booking">
            <Button>{t('bookingSuccess.makeNewBooking')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  ">
      {/* Hero Section */}
      <section className="relative h-64 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat mix-blend-overlay"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-01.jpg')`
          }}
        ></div>

        <div className="relative z-10 text-center text-white px-4">
          <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-200" />
          <h1 className="text-4xl md:text-6xl font-sofia font-bold mb-4 animate-fade-in-up">
            {t('bookingSuccess.title')}
          </h1>
          <p className="text-lg md:text-xl font-sofia font-light animate-fade-in-up opacity-90" style={{animationDelay: '0.2s'}}>
            {t('bookingSuccess.subtitle')}
          </p>
        </div>
      </section>

      {/* Booking Details */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-sofia font-bold text-[#81736d] mb-4">
              {t('bookingSuccess.bookingDetails')}
            </h2>
            <p className="text-lg text-text-secondary font-sofia">
              {t('bookingSuccess.bookingId')}: <span className="font-semibold text-accent">#{bookingData.booking_id || bookingData.id}</span>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Villa & Stay Details */}
            <Card className="animate-fade-in-up">
              <div className="p-6">
                <h3 className="text-xl font-sofia font-bold text-[#81736d] mb-6 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-accent" />
                  Villa & Stay Details
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-text-secondary font-sofia">{t('bookingSuccess.villa')}:</span>
                    <p className="font-sofia font-semibold text-lg">{bookingData.villa_display_name}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-text-secondary font-sofia">{t('bookingSuccess.checkIn')}:</span>
                      <p className="font-sofia font-medium flex items-center">
                        <Calendar className="h-4 w-4 mr-1 text-accent" />
                        {bookingData.check_in}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-text-secondary font-sofia">{t('bookingSuccess.checkOut')}:</span>
                      <p className="font-sofia font-medium flex items-center">
                        <Calendar className="h-4 w-4 mr-1 text-accent" />
                        {bookingData.check_out}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-sm text-text-secondary font-sofia">{t('bookingSuccess.guests')}:</span>
                    <p className="font-sofia font-medium flex items-center">
                      <Users className="h-4 w-4 mr-1 text-accent" />
                      {bookingData.guests} guests
                    </p>
                  </div>
                  
                  <div>
                    <span className="text-sm text-text-secondary font-sofia">{t('bookingSuccess.nights')}:</span>
                    <p className="font-sofia font-medium">{bookingData.nights} nights</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Guest Information */}
            <Card className="animate-fade-in-up" style={{animationDelay: '0.1s'}}>
              <div className="p-6">
                <h3 className="text-xl font-sofia font-bold text-[#81736d] mb-6 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-accent" />
                  Guest Information
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-text-secondary font-sofia">{t('bookingSuccess.name')}:</span>
                    <p className="font-sofia font-semibold text-lg">{bookingData.guest_full_name}</p>
                  </div>
                  
                  <div>
                    <span className="text-sm text-text-secondary font-sofia">{t('bookingSuccess.email')}:</span>
                    <p className="font-sofia font-medium flex items-center">
                      <Mail className="h-4 w-4 mr-1 text-accent" />
                      {bookingData.email}
                    </p>
                  </div>
                  
                  <div>
                    <span className="text-sm text-text-secondary font-sofia">{t('bookingSuccess.phone')}:</span>
                    <p className="font-sofia font-medium flex items-center">
                      <Phone className="h-4 w-4 mr-1 text-accent" />
                      {bookingData.phone}
                    </p>
                  </div>
                  
                  {bookingData.special_requests && (
                    <div>
                      <span className="text-sm text-text-secondary font-sofia">{t('bookingSuccess.specialRequests')}:</span>
                      <p className="font-sofia font-medium">{bookingData.special_requests}</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Services & Pricing */}
            {bookingData.selected_services && bookingData.selected_services.length > 0 && (
              <Card className="animate-fade-in-up" style={{animationDelay: '0.2s'}}>
                <div className="p-6">
                  <h3 className="text-xl font-sofia font-bold text-[#81736d] mb-6">
                    {t('bookingSuccess.services')}
                  </h3>
                  
                  <div className="space-y-3">
                    {bookingData.selected_services.map((service, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="font-sofia">{service.name}</span>
                        <span className="font-sofia font-medium">{formatMad(service.price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {/* Price Summary */}
            <Card className="animate-fade-in-up" style={{animationDelay: '0.3s'}}>
              <div className="p-6">
                <h3 className="text-xl font-sofia font-bold text-[#81736d] mb-6">
                  {t('bookingSuccess.pricingBreakdown')}
                </h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-sofia">Base Price ({bookingData.nights} nights):</span>
                    <span className="font-sofia font-medium">{formatMad(bookingData.base_price)}</span>
                  </div>
                  
                  {parseFloat(bookingData.services_total) > 0 && (
                    <div className="flex justify-between">
                      <span className="font-sofia">Additional Services:</span>
                      <span className="font-sofia font-medium">{formatMad(bookingData.services_total)}</span>
                    </div>
                  )}
                  
                  <div className="border-t border-gray-200 pt-3 flex justify-between text-lg">
                    <span className="font-sofia font-bold text-[#81736d]">Total Amount:</span>
                    <span className="font-sofia font-bold text-accent">{formatMad(bookingData.total_amount)}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Next Steps */}
          <Card className="mt-8 animate-fade-in-up" style={{animationDelay: '0.4s'}}>
            <div className="p-6 text-center">
              <h3 className="text-xl font-sofia font-bold text-[#81736d] mb-4">
                {t('bookingSuccess.nextSteps')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-accent font-sofia font-bold">1</span>
                  </div>
                  <h4 className="font-sofia font-semibold text-[#81736d] mb-2">{t('bookingSuccess.stepConfirm')}</h4>
                  <p className="text-sm text-text-secondary font-sofia">
                    {t('bookingSuccess.stepConfirmDesc')}
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-accent font-sofia font-bold">2</span>
                  </div>
                  <h4 className="font-sofia font-semibold text-[#81736d] mb-2">{t('bookingSuccess.stepPayment')}</h4>
                  <p className="text-sm text-text-secondary font-sofia">
                    {t('bookingSuccess.stepPaymentDesc')}
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-accent font-sofia font-bold">3</span>
                  </div>
                  <h4 className="font-sofia font-semibold text-[#81736d] mb-2">{t('bookingSuccess.stepEnjoy')}</h4>
                  <p className="text-sm text-text-secondary font-sofia">
                    {t('bookingSuccess.stepEnjoyDesc')}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left mb-8">
                <div className="p-4 rounded-lg border bg-white/50">
                  <h5 className="font-sofia font-semibold mb-1">{t('bookingSuccess.carRental')}</h5>
                  <p className="text-sm font-sofia text-text-secondary">{t('bookingSuccess.payProvider')}</p>
                  <p className="text-sm font-sofia mt-2"><span className="font-semibold">{t('bookingSuccess.phone')}:</span> +212 66 412 65 07</p>
                </div>
                <div className="p-4 rounded-lg border bg-white/50">
                  <h5 className="font-sofia font-semibold mb-1">{t('bookingSuccess.massage')}</h5>
                  <p className="text-sm font-sofia text-text-secondary">{t('bookingSuccess.payProvider')}</p>
                  <p className="text-sm font-sofia mt-2"><span className="font-semibold">{t('bookingSuccess.phone')}:</span> +212 66 412 65 07</p>
                </div>
                <div className="p-4 rounded-lg border bg-white/50">
                  <h5 className="font-sofia font-semibold mb-1">{t('bookingSuccess.transfer')}</h5>
                  <p className="text-sm font-sofia text-text-secondary">{t('bookingSuccess.payProvider')}</p>
                  <p className="text-sm font-sofia mt-2"><span className="font-semibold">{t('bookingSuccess.phone')}:</span> +212 66 412 65 07</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button variant="outline">{t('bookingSuccess.backToHome')}</Button>
                </Link>
                <Link to="/contact">
                  <Button>{t('common.contactUs')}</Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default BookingSuccessScreen;
